import { useState, type FormEvent } from 'react';
import { formatPhoneBR, simularParcela, whatsappLink, type TabelaTaxas } from '@agencia/shared';
import { Alert, Button } from '@/components/ui';
import { api } from '@/lib/api';
import { errorMessage } from '@/lib/errors';
import { formatBRL, formatDateTime, parseMoneyInput } from '@/lib/format';
import { useApi } from '@/lib/useApi';

interface Link {
  url: string;
  valorCartao: string;
  valorParcela: string;
  parcelas: number;
  valorPix: string;
  expiraEm: string;
}

/**
 * Gera o link de pagamento do Mercado Pago (cartão, em até 10x).
 * O consultor escolhe se o link cobra exatamente o valor ou soma as taxas do cartão.
 * Sem `valorPix` fixo, o próprio painel pede o valor.
 */
export function LinkPagamento({
  valorPix,
  descricaoInicial = '',
  leadId,
  telefone,
  onCreated,
  embutirTaxasPadrao = false,
  embutir: embutirControlado,
  onEmbutirChange,
  mostrarTabela = true,
  saleId,
  parcelaId,
  valorInicial,
  valorMax,
}: {
  saleId?: string;
  parcelaId?: string;
  /** Valor já preenchido, editável (ex.: saldo da parcela). */
  valorInicial?: string;
  /** Não deixa passar deste valor (saldo em aberto da parcela). */
  valorMax?: number;
  embutirTaxasPadrao?: boolean;
  /** Controlado por fora (o simulador mostra a tabela ao lado). */
  embutir?: boolean;
  onEmbutirChange?: (v: boolean) => void;
  mostrarTabela?: boolean;
  valorPix?: string | null;
  descricaoInicial?: string;
  leadId?: string;
  telefone?: string | null;
  onCreated?: () => void;
}) {
  const [raw, setRaw] = useState(valorInicial ? Number(valorInicial).toFixed(2).replace('.', ',') : '');
  const [descricao, setDescricao] = useState(descricaoInicial);
  const [link, setLink] = useState<Link | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copiado, setCopiado] = useState(false);
  const [embutirLocal, setEmbutirLocal] = useState(embutirTaxasPadrao);
  const embutir = embutirControlado ?? embutirLocal;
  const setEmbutir = (v: boolean) => (onEmbutirChange ? onEmbutirChange(v) : setEmbutirLocal(v));
  const { data: tabela } = useApi<TabelaTaxas>('/settings/mp-fees');
  const valorAtual = valorPix ?? parseMoneyInput(raw);
  // As duas opções lado a lado: o valor exato ou com as taxas do cartão somadas (10x).
  const opcoes = (() => {
    if (!tabela || !valorAtual || Number(valorAtual) <= 0) return null;
    try {
      const n = tabela.parcelamentoBp.length;
      return { exato: simularParcela(valorAtual, n, false, tabela), comTaxas: simularParcela(valorAtual, n, true, tabela) };
    } catch {
      return null;
    }
  })();

  // O link é "sem juros" (parcelado vendedor): o cliente paga o mesmo total em 1x ou 10x,
  // e o que a agência recebe muda conforme as parcelas que ele escolher.
  const escolhido = opcoes ? (embutir ? opcoes.comTaxas : opcoes.exato) : null;

  async function gerar(e: FormEvent) {
    e.preventDefault();
    const valor = valorAtual;
    if (!valor || Number(valor) <= 0) return setError('Digite o valor, ex.: 1.111,00.');
    if (valorMax !== undefined && Number(valor) > valorMax + 0.005) return setError(`O valor passa do saldo em aberto (${formatBRL(valorMax.toFixed(2))}).`);
    if (descricao.trim().length < 3) return setError('Descreva a viagem (ex.: Salvador ⇄ São Paulo · ida e volta).');
    setBusy(true);
    setError(null);
    try {
      setLink(await api.post<Link>('/crm/payment-links', { valorPix: valor, descricao: descricao.trim(), embutirTaxas: embutir, ...(leadId ? { leadId } : {}), ...(saleId && parcelaId ? { saleId, parcelaId } : {}) }));
      setCopiado(false);
      onCreated?.();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function copiar() {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link.url);
      setCopiado(true);
    } catch {
      setError('Não foi possível copiar. Selecione o link e copie manualmente.');
    }
  }

  const mensagem = link
    ? `Segue o link para pagamento no cartão: ${formatBRL(link.valorCartao)} em até ${link.parcelas}x sem juros de ${formatBRL(link.valorParcela)}.\n${link.url}`
    : '';

  return (
    <form className="pay-link" onSubmit={(e) => void gerar(e)}>
      <h3>Link de pagamento (Mercado Pago)</h3>
      <p className="muted small">Pagamento no cartão em até 10x. O link vale por 3 dias.</p>
      {error && <Alert>{error}</Alert>}
      {!valorPix && (
        <div className="field">
          <label htmlFor={`pl-valor-${leadId ?? 'sim'}`}>{saleId ? 'Quanto desta parcela vai no cartão' : 'Valor'}</label>
          <input id={`pl-valor-${leadId ?? 'sim'}`} inputMode="decimal" placeholder="R$ 0,00" value={raw} onChange={(e) => setRaw(e.target.value)} />
          {valorMax !== undefined && <span className="muted small">Máximo: {formatBRL(valorMax.toFixed(2))} (em aberto nesta parcela)</span>}
        </div>
      )}
      <div className="field">
        <label htmlFor={`pl-desc-${leadId ?? 'sim'}`}>O que o cliente está pagando</label>
        <input
          id={`pl-desc-${leadId ?? 'sim'}`}
          maxLength={120}
          placeholder="Ex.: Passagens Salvador ⇄ São Paulo · 2 adultos"
          value={descricao}
          onChange={(e) => setDescricao(e.target.value)}
        />
      </div>
      {opcoes ? (
        <div className="pay-link__opts" role="radiogroup" aria-label="Valor do link">
          <label className={`pay-link__opt ${!embutir ? 'is-on' : ''}`}>
            <input type="radio" name={`pl-modo-${leadId ?? 'sim'}`} checked={!embutir} onChange={() => setEmbutir(false)} />
            <span className="pay-link__opt-t">Valor digitado</span>
            <strong>{formatBRL(opcoes.exato.clientePaga)}</strong>
            <span>
              {opcoes.exato.parcelas}x de {formatBRL(opcoes.exato.valorParcela)}
            </span>
            <span className="muted small">Agência recebe ~{formatBRL(opcoes.exato.voceRecebe)} em {opcoes.exato.parcelas}x</span>
          </label>
          <label className={`pay-link__opt ${embutir ? 'is-on' : ''}`}>
            <input type="radio" name={`pl-modo-${leadId ?? 'sim'}`} checked={embutir} onChange={() => setEmbutir(true)} />
            <span className="pay-link__opt-t">Com as taxas do cartão</span>
            <strong>{formatBRL(opcoes.comTaxas.clientePaga)}</strong>
            <span>
              {opcoes.comTaxas.parcelas}x de {formatBRL(opcoes.comTaxas.valorParcela)}
            </span>
            <span className="muted small">Agência recebe {formatBRL(opcoes.comTaxas.voceRecebe)}</span>
          </label>
        </div>
      ) : (
        !valorPix && <p className="muted small">Digite o valor para ver quanto fica em 10x, com e sem as taxas.</p>
      )}
      {mostrarTabela && tabela && escolhido && (
        <details className="pay-link__table">
          <summary>Ver de 1x a {tabela.parcelamentoBp.length}x (o que o cliente paga e o que a agência recebe)</summary>
          <TabelaParcelas totalCliente={escolhido.clientePaga} tabela={tabela} />
        </details>
      )}
      <Button type="submit" variant={link ? 'secondary' : 'primary'} loading={busy}>
        {link ? 'Gerar outro link' : 'Gerar link de pagamento'}
      </Button>
      {link && (
        <div className="pay-link__result">
          <strong>
            {formatBRL(link.valorCartao)} em até {link.parcelas}x de {formatBRL(link.valorParcela)}
          </strong>
          <input className="pay-link__url" readOnly value={link.url} onFocus={(e) => e.currentTarget.select()} aria-label="Link de pagamento" />
          <div className="row tight">
            <Button type="button" onClick={() => void copiar()}>
              {copiado ? 'Copiado!' : 'Copiar link'}
            </Button>
            {telefone && (
              <a className="btn btn--secondary" href={whatsappLink(telefone, mensagem)} target="_blank" rel="noreferrer">
                Enviar no WhatsApp ({formatPhoneBR(telefone)})
              </a>
            )}
            <a className="btn btn--ghost" href={link.url} target="_blank" rel="noreferrer">
              Abrir
            </a>
          </div>
          <span className="muted small">Válido até {formatDateTime(link.expiraEm)}.</span>
        </div>
      )}
    </form>
  );
}

/**
 * Link sem juros (parcelado vendedor): o cliente paga o mesmo total em 1x ou 10x,
 * e o que a agência recebe muda conforme as parcelas que ele escolher.
 */
export function TabelaParcelas({ totalCliente, tabela }: { totalCliente: string; tabela: TabelaTaxas }) {
  const linhas = tabela.parcelamentoBp.map((_, i) => simularParcela(totalCliente, i + 1, false, tabela));
  return (
    <div className="pay-link__table">
      <table>
        <thead>
          <tr>
            <th>Parcelas</th>
            <th className="num">Cliente paga</th>
            <th className="num">Agência recebe</th>
          </tr>
        </thead>
        <tbody>
          {linhas.map((o) => (
            <tr key={o.parcelas}>
              <td>{o.parcelas === 1 ? 'À vista' : `${o.parcelas}x de ${formatBRL(o.valorParcela)}`}</td>
              <td className="num">{formatBRL(o.clientePaga)}</td>
              <td className="num">{formatBRL(o.voceRecebe)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="muted small">O cliente paga o mesmo total em qualquer número de parcelas (sem juros). As taxas de parcelamento ficam com a agência.</p>
    </div>
  );
}
